/**
 * Premium Route Component
 * Requires authentication and either:
 * - Launch Grace Period (free until April 1, 2026), OR
 * - Premium subscription, OR
 * - Active trial (30 days from signup), OR
 * - Admin status
 * 
 * After trial expires, user can only access Shopping List (free tier)
 */

import { Navigate, useLocation, Link } from 'react-router-dom';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAppConfig } from '@/contexts/AppConfigContext.jsx';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

const PremiumRoute = ({ children, redirectTo = '/signin' }) => {
  const { 
    currentUser, 
    loading, 
    isPremium, 
    isTrialing,
    hasFullAccess,
    isAdmin,
    isGracePeriod, // Launch Grace Period (free until April 1, 2026)
    refreshPremiumClaim, 
    trialInfo 
  } = useAuth();
  const { premiumCopy } = useAppConfig();
  const location = useLocation();
  const [checking, setChecking] = useState(false);
  const [firestoreTrialEnd, setFirestoreTrialEnd] = useState(null);
  const [firestoreChecked, setFirestoreChecked] = useState(false);
  const refreshedOnce = useRef(false);
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Fallback: Check Firestore directly for trial info if API failed
  useEffect(() => {
    const checkFirestoreTrial = async () => {
      if (currentUser && !trialInfo && !firestoreChecked) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const billing = userDoc.data()?.billing;
            if (billing?.trialEnd) {
              setFirestoreTrialEnd(Number(billing.trialEnd));
              console.log('📅 Firestore fallback trial info:', { trialEnd: billing.trialEnd });
            }
          }
        } catch (e) {
          console.warn('Failed to check Firestore for trial info', e);
        }
        setFirestoreChecked(true);
      }
    };
    checkFirestoreTrial();
  }, [currentUser, trialInfo, firestoreChecked]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // ============================================================================
  // LAUNCH GRACE PERIOD (TOP-LEVEL OVERRIDE)
  // All features free until April 1, 2026 - does NOT modify billing logic
  // ============================================================================
  if (isGracePeriod) {
    return children;
  }

  // Admin users always have full access - no trial or subscription checks needed
  if (isAdmin) {
    return children;
  }

  // Premium users always have access
  if (isPremium) {
    return children;
  }

  // User has full access if in active trial (from AuthContext)
  if (hasFullAccess || isTrialing) {
    return children;
  }

  // Calculate trial status from trialInfo OR Firestore fallback
  const nowTs = Date.now();
  const rawEnd = Number(trialInfo?.trialEnd) || firestoreTrialEnd || 0;
  const effectiveEnd = Number(trialInfo?.effectiveTrialEnd || rawEnd);
  const onTrial = effectiveEnd > nowTs; // active trial or grace

  // If we have valid trial data showing active trial, allow access
  if (onTrial) {
    return children;
  }

  // CRITICAL: Still checking Firestore AND no trial info from API? Show loading, NOT trial ended!
  // This prevents the "Trial Ended" flash when backend is slow or offline
  if (!firestoreChecked && !trialInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Trial expired and not premium - check if returning from Stripe
  const status = query.get('status');
  if (status === 'success' && !refreshedOnce.current && !checking) {
    refreshedOnce.current = true;
    setChecking(true);
    refreshPremiumClaim().finally(() => setChecking(false));
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 transition-colors">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Trial expired and not premium - show upgrade prompt
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 p-6 transition-colors">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 shadow-xl rounded-2xl p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
          <span className="text-3xl">⏰</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Trial Ended</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Your 30-day trial has ended. Upgrade to Premium to unlock all FinList features, 
          or continue using Shopping List for free.
        </p>
        <div className="flex flex-col gap-3">
          <Link
            to="/profile"
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-white font-medium hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg"
          >
            Upgrade to Premium
          </Link>
          <Link
            to="/shopping-lists"
            className="inline-flex items-center justify-center rounded-xl bg-gray-100 dark:bg-slate-700 px-6 py-3 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-slate-600 transition-all"
          >
            Continue with Shopping List
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PremiumRoute;
